import type { GenericFieldConfig } from '@neo/blocks/forms/types'
import { useMemo } from 'react'
import { useAccountNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import { useWhatsAppTemplatesQuery } from 'v2/pages/settings/whatsapp/templates/queries'
import { AgentChannels } from 'v2/types'
import { ConvChannels } from 'v2/types/api/conversations'
import { useFlowStore } from '../../store'
import type {
  AgentPresetConversationNodeData,
  AgentPresetConversationNodeformData,
  SidebarComponentProps,
} from '../../types'
import { GenericSidebarComponent } from '../sidebar.base'
import { chatSpecificFields } from './chat-config'
import { commonFields, textFields, waitForResponseFields } from './common-config'
import { messageEventsFields, messageSpecificFields } from './message-config'
import { whatsappSpecificFields } from './whatsapp-config'

const fields = (
  nodeVars: string[],
  fieldsWithDynamicNodeVars: Record<string, string[]>,
): GenericFieldConfig<AgentPresetConversationNodeformData> => {
  const commonFieldsList = commonFields(nodeVars)
  const messageFields = messageSpecificFields(nodeVars, fieldsWithDynamicNodeVars)
  const whatsappFields = whatsappSpecificFields(nodeVars, fieldsWithDynamicNodeVars)
  const chatFields = chatSpecificFields(nodeVars)
  const waitFields = waitForResponseFields(nodeVars)
  const textField = textFields(nodeVars)
  const eventsField = messageEventsFields(nodeVars)

  return [
    ...commonFieldsList,
    ...messageFields,
    ...whatsappFields,
    ...chatFields,
    ...textField,
    ...waitFields,
    ...eventsField,
  ] as GenericFieldConfig<AgentPresetConversationNodeformData>
}

export const AgentPresetConversationSidebarComponent = (
  props: SidebarComponentProps<AgentPresetConversationNodeData>,
) => {
  const { nodeVars, selectedNode } = props
  const { data: templatesResponse } = useWhatsAppTemplatesQuery()
  const { data: accountNumbers } = useAccountNumbersQuery('simple', ConvChannels.MESSAGE)

  const fieldsWithDynamicNodeVars = useMemo(() => {
    const whatsappSenderIds = templatesResponse?.map((template) => template.sender_ids)
    const messageSenderIds = accountNumbers
      ?.filter((number) => number.tendlc_campaign_id || number.number_type === 'tollfree')
      .map((number) => number.number)
    return {
      whatsapp_from: [...(whatsappSenderIds?.flat() || []), ...nodeVars],
      message_from: [...(messageSenderIds?.flat() || []), ...nodeVars],
    }
  }, [templatesResponse, accountNumbers, nodeVars])

  const formFields = fields(nodeVars, fieldsWithDynamicNodeVars)

  // Memoize defaultValues based only on selectedNode, not agentMeta
  // This prevents re-renders when agentMeta changes
  const defaultValues = useMemo(() => {
    const currentAgentMeta = useFlowStore.getState().agentMeta

    let { defaultChannel } = currentAgentMeta
    if (defaultChannel === 'call')
      defaultChannel = (selectedNode.data.config.channel as AgentChannels) || AgentChannels.whatsapp

    const defaults: Partial<AgentPresetConversationNodeformData> = {
      channel: defaultChannel as 'message' | 'whatsapp' | 'chat',
    }

    // Reply flows default `from` to the number the customer wrote to; anything
    // else stays empty for the user to pick. Never fall back into nodeVars: the
    // sender-id list is async and can be empty, and its nodeVars tail leaks
    // junk like {{Start.request_type}} into the field (SER-6104).
    if (defaultChannel === 'whatsapp') {
      if (!selectedNode.data.config.from)
        defaults.from = nodeVars.includes('{{Start.whatsapp.to}}') ? '{{Start.whatsapp.to}}' : ''
      if (!selectedNode.data.config.to)
        defaults.to = nodeVars.includes('{{Start.whatsapp.from}}') ? '{{Start.whatsapp.from}}' : ''
    } else if (defaultChannel === 'message') {
      if (!selectedNode.data.config.from)
        defaults.from = nodeVars.includes('{{Start.message.to}}') ? '{{Start.message.to}}' : ''
      if (!selectedNode.data.config.to)
        defaults.to = nodeVars.includes('{{Start.message.from}}') ? '{{Start.message.from}}' : ''
    }

    return defaults
  }, [selectedNode.data.config.channel])

  return (
    <GenericSidebarComponent
      fields={formFields}
      {...props}
      className="relative"
      defaultValues={defaultValues}
    />
  )
}
